import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, User, ArrowRight, MessageSquare } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Seo from "../components/Seo";

export default function BlogDetail() {
  const { slug } = useParams();
  const { settings } = useApp();
  const { data: b, isLoading } = useQuery({ queryKey: ["blog", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data });

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-16"><div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /></div>;
  if (!b) return <div className="py-24 text-center text-slate-500">Article not found.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Seo title={b.seo_title || b.title} description={b.meta_description || b.excerpt} />
      <div className="text-sm text-slate-500 mb-4"><Link to="/blog" className="hover:text-indigo-800">Guides</Link> / <span>{b.category}</span></div>
      {b.category && <span className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">{b.category}</span>}
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 leading-tight">{b.title}</h1>
      <div className="flex items-center gap-4 text-sm text-slate-500 mt-4">
        <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {b.author}</span>
        {b.published_at && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Updated {new Date(b.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
      </div>
      {b.featured_image && <img src={fileUrl(b.featured_image)} alt={b.image_alt || b.title} className="w-full rounded-2xl mt-6 aspect-[16/9] object-cover bg-slate-100" />}

      <article className="blog-content mt-8" dangerouslySetInnerHTML={{ __html: b.content_html }} />

      {b.faqs?.length ? (
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible>{b.faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq${i}`}><AccordionTrigger className="text-left">{f.question}</AccordionTrigger><AccordionContent>{f.answer}</AccordionContent></AccordionItem>
          ))}</Accordion>
        </section>
      ) : null}

      {/* CTA */}
      <div className="mt-12 bg-indigo-900 text-white rounded-2xl p-8 text-center">
        <h3 className="font-heading text-2xl font-bold">Looking for verified properties in Faridabad?</h3>
        <p className="text-indigo-100/90 mt-2">Explore our handpicked, personally verified inventory.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-5">
          <Link to="/properties"><Button className="bg-white text-indigo-900 hover:bg-indigo-50">See Available Properties</Button></Link>
          <a href={`https://wa.me/${settings.whatsapp || ""}`} target="_blank" rel="noreferrer"><Button variant="outline" className="border-white text-white hover:bg-indigo-800"><MessageSquare className="w-4 h-4 mr-2" /> WhatsApp Us</Button></a>
        </div>
      </div>

      {b.related?.length ? (
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Read Next</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {b.related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all">
                <div className="text-xs text-indigo-800 font-semibold">{r.category}</div>
                <div className="font-heading font-bold text-slate-900 mt-1 group-hover:text-indigo-800 line-clamp-2">{r.title}</div>
                <div className="text-sm text-indigo-800 flex items-center gap-1 mt-2">Read <ArrowRight className="w-4 h-4" /></div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
